/* ═══════════════════════════════════════════════════════════════
   Canvas token selection — Explorer semantics on the forma paper.

   The canvas selects TOKENS, and a token's key already names the list it
   belongs to (`token:<zoneKey>:<idx>`). So a Shift range is exactly a
   range over that ZONE'S token list — the stable array the paper renders
   from, never the order the DOM happens to be queried in.

   The rules themselves are not re-written here: they are the app-wide
   `nextSelection` in `renderer/lib/listSelection.ts`, the same proof the
   dirty reader and the Spec Studio's block layer stand on
   (Phase 1 item 5, docs/MASTERS_UNIFIED_PLAN.md).
   ═══════════════════════════════════════════════════════════════ */

import type { StandardConfig } from '../../shared/types';
import { nextSelectionByKey, type SelectionMods } from '../lib/listSelection';
import { parseTokenKey, readZone } from './formaZonesModel';

/** Every token key of the zone `key` lives in, in the zone's own order. */
export function zoneTokenKeys(forma: StandardConfig | null, key: string): string[] {
  if (!forma || !key.startsWith('token:')) return [];
  const { zoneKey } = parseTokenKey(key);
  return readZone(forma, zoneKey).map((_v, i) => `token:${zoneKey}:${i}`);
}

/**
 * What clicking token `key` with these modifiers selects.
 *
 * - plain      → that token alone
 * - Ctrl/Cmd   → toggle it, keeping everything else (any zone)
 * - Shift      → the range from the anchor THROUGH this token, inside the
 *                anchor's own zone, replacing the rest of the selection
 * - Ctrl+Shift → that range added to what is already selected
 *
 * A Shift click whose anchor sits in another zone has no range to run, so it
 * degrades to a plain click rather than sweeping up a zone the user is not
 * pointing at.
 */
export function nextTokenSelection(
  forma: StandardConfig | null,
  prev: string[],
  anchorKey: string | null,
  key: string,
  mods: SelectionMods
): { selection: string[]; anchor: string | null } {
  return nextSelectionByKey(prev, anchorKey, key, zoneTokenKeys(forma, key), mods);
}
